import { PayloadAction } from "@reduxjs/toolkit";
import { eventChannel, END } from "redux-saga";
import { takeLatest, all, call, put, take, fork } from "redux-saga/effects";

import { IProgressState } from "infrastructure/helpers/common";
import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { notification } from "infrastructure/notification";
import { createNotificationResult } from "infrastructure/store";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { messagesUploadActions } from "./MessagesUpload.actions";
import { messagesUploadApi as api } from "./MessagesUpload.api";
import { getValidationIssues } from "./MessagesUpload.helpers";

const createUploadChannel = (file: File) => {
  let emit;

  const channel = eventChannel(emitter => {
    emit = emitter;

    return () => {};
  });

  const promise = call(api.uploadMessages, file, (progress: IProgressState) => {
    emit(progress.value);

    if (progress.completed) {
      emit(END);
    }
  });

  return [promise, channel];
};

function* progressListener(channel) {
  while (true) {
    const data = yield take(channel);

    yield put(messagesUploadActions.uploadMessagesProcessing(data));
  }
}

function* uploadMessagesExecute(action: PayloadAction<File[]>) {
  try {
    const file = action.payload[0];

    if (file != null) {
      const [promise, channel] = createUploadChannel(file);
      yield fork(progressListener, channel);

      yield promise;

      yield put(messagesUploadActions.uploadMessagesCompleted());
    } else {
      yield put(messagesUploadActions.uploadMessagesError(notification.error().useMessageId(displayMessages.error("messagesUploadFileRequired"))));
    }
  } catch (error) {
    if (error instanceof HttpError) {
      if (error.statusCode === HttpStatusCodes.BadRequest) {
        yield put(
          messagesUploadActions.uploadMessagesFailed(
            createNotificationResult({
              validationIssues: getValidationIssues(error.data),
              text: error.message
            }).withNotification(notification.error().useMessageId(displayMessages.error("messagesUpload")))
          )
        );
      }
    } else {
      yield put(messagesUploadActions.uploadMessagesError(notification.error().useMessageId(displayMessages.error("messagesUploadUnexpected"))));
    }
  }
}

function* actionWatcher() {
  yield takeLatest(messagesUploadActions.uploadMessages.toString(), uploadMessagesExecute);
}

export function* messagesUploadSaga() {
  yield all([actionWatcher()]);
}
